/**
 * Sticky Add to Cart JavaScript Module
 * Handles scroll detection, independent variant selection, image/price updates, and form submission
 * 
 * CRITICAL: Sticky bar operates completely independently of the main product form after initial sync.
 */

class StickyATC {
  constructor() {
    this.stickyBar = document.getElementById('sticky-add-to-cart');
    // Support both standard Shopify forms and theme's custom product-form-component
    this.mainForm = document.querySelector('product-form-component') ||
                   document.querySelector('form[action*="/cart/add"]:not(#sticky-product-form)');
    // Theme uses ref="addToCartButton" on the button; also support name="add" fallback
    this.mainATC = this.mainForm?.querySelector('[ref="addToCartButton"]') ||
                  this.mainForm?.querySelector('[name="add"]');
    this.variantSelect = this.stickyBar?.querySelector('select[name="id"]');
    this.stickyImage = this.stickyBar?.querySelector('.sticky-add-to-cart__img');
    this.stickyPriceElement = this.stickyBar?.querySelector('[data-sticky-price]');
    this.stickyQuantityInput = this.stickyBar?.querySelector('[data-sticky-quantity-input]');
    this.stickyStockMessage = this.stickyBar?.querySelector('[data-sticky-stock-message]');
    this.stickyVariantPreviewImg = this.stickyBar?.querySelector('.sticky-add-to-cart__variant-preview-img');
    this.isVisible = false;
    this.isSubmitting = false;
    this.triggerOffset = 200; // Default offset
    this.cookieBannerHeight = 0;
    
    // Bind methods
    this.handleScroll = this.throttle(this.handleScroll.bind(this), 16);
    this.handleQuantityChange = this.debounce(this.handleQuantityChange.bind(this), 300);
    this.handleSubmit = this.handleSubmit.bind(this);
    
    this.init();
  }
  
  init() {
    if (!this.stickyBar) return;
    
    // Get settings from data attributes
    this.triggerOffset = parseInt(this.stickyBar.dataset.scrollThreshold) || 200;
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Setup cookie banner detection
    this.setupCookieBannerDetection();
    
    // Setup quantity sync (stock limit enforcement)
    this.setupQuantitySync();
    
    // Initial sync (optional - can be removed for full independence)
    this.syncWithMainForm();
  }
  
  setupEventListeners() {
    // Scroll detection
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Quantity input
    if (this.stickyQuantityInput) {
      this.stickyQuantityInput.addEventListener('input', this.handleQuantityChange);
    }
    
    // Form submission
    const stickyForm = this.stickyBar.querySelector('form');
    if (stickyForm) {
      stickyForm.addEventListener('submit', this.handleSubmit);
    }
    
    // Variant change detection (independent operation)
    this.setupVariantDetection();
  }
  
  setupVariantDetection() {
    // Sticky bar variant selector works completely independently
    // No need to listen to main form changes - they operate separately
    
    // Listen for sticky variant selector changes (independent operation)
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', (e) => {
        // Sticky bar variant change works independently
        // Does NOT affect main form - complete independence
        this.handleStickyVariantChange(e);
      });
    }
  }
  
  handleStickyVariantChange(event) {
    const selectedOption = event.target.options[event.target.selectedIndex];
    const variantId = selectedOption.value;
    const variantImageUrl = selectedOption.dataset.img;
    const variantPrice = selectedOption.dataset.price;
    const variantAvailable = selectedOption.dataset.available === 'true';
    const variantStock = parseInt(selectedOption.dataset.inventoryQuantity, 10) || 9999;
    
    // 1. Update sticky bar image
    if (this.stickyImage && variantImageUrl) {
      this.stickyImage.src = variantImageUrl;
    }

    // 1b. Update variant preview thumbnail
    const previewImgUrl = selectedOption.dataset.previewImg;
    if (this.stickyVariantPreviewImg && previewImgUrl) {
      this.stickyVariantPreviewImg.src = previewImgUrl;
      this.stickyVariantPreviewImg.alt = selectedOption.textContent.trim();
    }
    
    // 2. Update sticky bar price
    if (this.stickyPriceElement && variantPrice) {
      this.stickyPriceElement.textContent = variantPrice;
    }
    
    // 3. Update sticky bar variant ID attribute (for reference)
    this.stickyBar.dataset.variantId = variantId;
    
    // 4. Update the hidden input in sticky form (for add to cart)
    const stickyForm = this.stickyBar.querySelector('form');
    if (stickyForm) {
      const hiddenVariantInput = stickyForm.querySelector('input[name="id"][type="hidden"]');
      if (hiddenVariantInput) {
        hiddenVariantInput.value = variantId;
      } else {
        // If using select directly for form submission, update the select value
        this.variantSelect.value = variantId;
      }
    }
    
    // 5. Update Quantity Max, Input Value, and Stock Message (CRITICAL NEW LOGIC)
    if (this.stickyQuantityInput) {
      this.stickyQuantityInput.setAttribute('max', variantStock);
      
      // Ensure current quantity is not more than stock
      const currentQty = parseInt(this.stickyQuantityInput.value, 10);
      if (currentQty > variantStock) {
        this.stickyQuantityInput.value = variantStock > 0 ? variantStock : 1;
      }
    }
    
    // 6. Update Stock Message
    if (this.stickyStockMessage) {
      if (variantStock > 0 && variantStock <= 5) {
        this.stickyStockMessage.textContent = `Only ${variantStock} items available.`;
      } else if (variantStock === 0 && !variantAvailable) {
        this.stickyStockMessage.textContent = `Sold out.`;
      } else {
        this.stickyStockMessage.textContent = '';
      }
    }
    
    // 7. Update ATC button state
    const atcButton = stickyForm?.querySelector('[type="submit"]');
    if (atcButton) {
      atcButton.disabled = !variantAvailable;
      const buttonText = atcButton.querySelector('.sticky-add-to-cart__button-text');
      if (buttonText) {
        buttonText.textContent = variantAvailable ? this.getButtonText() : 'Sold out';
      }
    }
    
    // NOTE: Do NOT update main form - sticky bar works independently
  }
  
  updateButtonAvailability(variantId) {
    const submitBtn = this.stickyBar.querySelector('.sticky-add-to-cart__button');
    const buttonText = this.stickyBar.querySelector('.sticky-add-to-cart__button-text');
    
    if (submitBtn && buttonText) {
      // Check availability from product data or assume available
      // You can enhance this to check actual variant availability
      const isAvailable = true; // Default to available
      
      if (isAvailable) {
        submitBtn.disabled = false;
        buttonText.textContent = this.getButtonText();
      } else {
        submitBtn.disabled = true;
        buttonText.textContent = 'Sold Out';
      }
    }
  }
  
  setupCookieBannerDetection() {
    // Use MutationObserver to detect cookie banner
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          this.detectCookieBanner();
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Initial check
    this.detectCookieBanner();
  }
  
  detectCookieBanner() {
    const cookieBanner = document.querySelector('[data-cookie-banner], .cookie-banner, #cookie-banner');
    if (cookieBanner && this.stickyBar) {
      this.cookieBannerHeight = cookieBanner.offsetHeight;
      this.stickyBar.classList.add('with-cookie-banner');
    } else if (this.stickyBar) {
      this.cookieBannerHeight = 0;
      this.stickyBar.classList.remove('with-cookie-banner');
    }
  }
  
  handleScroll() {
    // If no main ATC found, use scroll position fallback
    if (!this.mainATC) {
      const shouldShow = window.scrollY > this.triggerOffset;
      if (shouldShow && !this.isVisible) {
        this.show();
      } else if (!shouldShow && this.isVisible) {
        this.hide();
      }
      return;
    }

    const mainATCRect = this.mainATC.getBoundingClientRect();
    // Show when the main ATC button has scrolled above the viewport top (out of view)
    const shouldShow = mainATCRect.bottom < 0;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }
  
  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    this.stickyBar.style.display = 'block';

    // Double rAF ensures the browser has rendered display:block
    // before starting the CSS transform transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.stickyBar.classList.add('visible');
      });
    });
  }
  
  hide() {
    if (!this.isVisible) return;
    
    this.isVisible = false;
    this.stickyBar.classList.remove('visible');
    
    // Hide after animation
    setTimeout(() => {
      if (!this.isVisible) {
        this.stickyBar.style.display = 'none';
      }
    }, 300);
  }
  
  syncWithMainForm() {
    // Optional initial sync - only runs once on page load
    // After this, sticky bar and main form work independently

    // Ensure the sticky bar always has a variant ID set from its own data attribute
    if (!this.stickyBar.dataset.variantId && this.variantSelect?.value) {
      this.stickyBar.dataset.variantId = this.variantSelect.value;
    }

    if (!this.mainForm) return;

    // Sync variant (initial sync only)
    // Support both standard hidden input and theme's product-form-component data attribute
    const variantInput = this.mainForm.querySelector('input[name="id"]:checked, select[name="id"]') ||
                         this.mainForm.querySelector('input[name="id"]');
    // Also check product-form-component's data-current-variant-id attribute
    const variantIdFromComponent = this.mainForm.dataset?.currentVariantId;
    if (variantInput && this.variantSelect) {
      const variantId = variantInput.value || variantInput.selectedOptions?.[0]?.value || variantIdFromComponent;
      if (variantId) {
        this.stickyBar.dataset.variantId = variantId;
        this.variantSelect.value = variantId;
        
        // Update hidden input
        const stickyForm = this.stickyBar.querySelector('form');
        if (stickyForm) {
          const hiddenVariantInput = stickyForm.querySelector('input[name="id"][type="hidden"]');
          if (hiddenVariantInput) {
            hiddenVariantInput.value = variantId;
          }
        }
        
        // Update image if data-img attribute exists
        const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        if (selectedOption && selectedOption.dataset.img && this.stickyImage) {
          this.stickyImage.src = selectedOption.dataset.img;
        }

        // Update variant preview thumbnail
        if (selectedOption && selectedOption.dataset.previewImg && this.stickyVariantPreviewImg) {
          this.stickyVariantPreviewImg.src = selectedOption.dataset.previewImg;
          this.stickyVariantPreviewImg.alt = selectedOption.textContent.trim();
        }

        // Update price if data-price attribute exists
        if (selectedOption && selectedOption.dataset.price && this.stickyPriceElement) {
          this.stickyPriceElement.textContent = selectedOption.dataset.price;
        }
      }
    }
    
    // Sync quantity (optional - can work independently too)
    const mainQuantity = this.mainForm.querySelector('input[name="quantity"]');
    if (mainQuantity && this.stickyQuantityInput) {
      const mainValue = parseInt(mainQuantity.value, 10);
      const max = parseInt(this.stickyQuantityInput.getAttribute('max'), 10) || 9999;
      // Ensure synced value doesn't exceed stock
      this.stickyQuantityInput.value = Math.min(mainValue, max);
    }
    
    // Update stock message on initial load
    if (this.variantSelect && this.stickyStockMessage) {
      const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
      if (selectedOption) {
        const variantStock = parseInt(selectedOption.dataset.inventoryQuantity, 10) || 9999;
        const variantAvailable = selectedOption.dataset.available === 'true';
        
        if (variantStock > 0 && variantStock <= 5) {
          this.stickyStockMessage.textContent = `Only ${variantStock} items available.`;
        } else if (variantStock === 0 && !variantAvailable) {
          this.stickyStockMessage.textContent = `Sold out.`;
        } else {
          this.stickyStockMessage.textContent = '';
        }
      }
    }
  }
  
  handleQuantityChange(event) {
    // This method is debounced, but we'll handle validation in setupQuantitySync
    // which handles both input and blur events more reliably
  }
  
  setupQuantitySync() {
    const stickyQuantityInput = this.stickyQuantityInput;
    
    if (!stickyQuantityInput) return;
    
    // Fix: Prevent over-ordering and the aggressive reset behavior on blur/change
    const quantityCheck = () => {
      let value = parseInt(stickyQuantityInput.value, 10);
      const max = parseInt(stickyQuantityInput.getAttribute('max'), 10) || 9999;
      const min = parseInt(stickyQuantityInput.getAttribute('min'), 10) || 1;
      
      if (isNaN(value) || value < min) {
        // This stops the input value from being 0 or aggressively resetting to 1 while typing.
        // On blur, if the value is empty or 0, set it to min (1).
        if (document.activeElement !== stickyQuantityInput) {
          stickyQuantityInput.value = min;
        }
      } else if (value > max) {
        stickyQuantityInput.value = max; // Cap at max stock
      }
    };
    
    // Listen for input and blur events on the sticky input
    stickyQuantityInput.addEventListener('input', quantityCheck);
    stickyQuantityInput.addEventListener('blur', quantityCheck);
    
    // Initial value check
    quantityCheck();
  }
  
  syncQuantityToMainForm(quantity) {
    const mainQuantity = this.mainForm?.querySelector('input[name="quantity"]');
    if (mainQuantity) {
      mainQuantity.value = quantity;
    }
  }
  
  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.setSubmittingState(true);
    this.hideErrorMessage();

    try {
      const stickyForm = this.stickyBar.querySelector('form');
      const variantId = this.stickyBar.dataset.variantId || this.variantSelect?.value;
      const quantity = parseInt(this.stickyQuantityInput?.value || '1', 10) || 1;

      if (!variantId) {
        this.showErrorMessage('Please select a variant');
        return;
      }

      // Use AJAX cart add to avoid full-page redirect
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Could not add to cart');
      }

      // Fetch the updated cart to get itemCount, then dispatch theme's cart:update event
      const cartResponse = await fetch('/cart.js');
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      const itemCount = cart ? quantity : 0;

      // Dispatch theme's native CartUpdateEvent structure so cart-icon.js updates the bubble
      const cartUpdateEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'sticky-add-to-cart',
          data: {
            itemCount: quantity,
            source: 'product-form-component',
            variantId: String(variantId),
          },
        },
      });
      document.dispatchEvent(cartUpdateEvent);

      // Also dispatch cart:updated for any legacy listeners
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    } catch (error) {
      console.error('Sticky ATC submission error:', error);
      this.showErrorMessage(error.message || 'Error adding to cart');
    } finally {
      this.setSubmittingState(false);
      this.isSubmitting = false;
    }
  }
  
  setSubmittingState(isSubmitting) {
    const submitBtn = this.stickyBar.querySelector('.sticky-add-to-cart__button');
    const buttonText = this.stickyBar.querySelector('.sticky-add-to-cart__button-text');
    const buttonSpinner = this.stickyBar.querySelector('.sticky-add-to-cart__button-spinner');
    
    if (submitBtn) {
      submitBtn.disabled = isSubmitting;
    }
    
    if (buttonText && buttonSpinner) {
      buttonText.style.display = isSubmitting ? 'none' : 'block';
      buttonSpinner.style.display = isSubmitting ? 'block' : 'none';
    }
  }
  
  async waitForCartUpdate() {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve();
      }, 5000);
      
      const handleCartUpdate = () => {
        clearTimeout(timeout);
        document.removeEventListener('cart:updated', handleCartUpdate);
        resolve();
      };
      
      document.addEventListener('cart:updated', handleCartUpdate);
    });
  }
  
  getButtonText() {
    return 'Add to cart';
  }
  
  checkForInventoryErrors() {
    // Check main form for error messages
    if (!this.mainForm) return;
    
    const mainFormContainer = this.mainForm.closest('[data-section-id]') || this.mainForm.parentElement;
    const errorElements = mainFormContainer.querySelectorAll('status, [role="status"], .product-form__error-message');
    
    errorElements.forEach(el => {
      const errorText = el.textContent.trim();
      
      // Check for inventory-related errors
      if (errorText && (
        errorText.toLowerCase().includes('inventory') ||
        errorText.toLowerCase().includes('stock') ||
        errorText.toLowerCase().includes('availab') ||
        errorText.toLowerCase().includes('only') ||
        errorText.toLowerCase().includes('maximum')
      )) {
        this.showErrorMessage(errorText);
      }
    });
  }
  
  showErrorMessage(message) {
    // Remove any existing error message
    this.hideErrorMessage();
    
    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'sticky-add-to-cart__error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.setAttribute('aria-live', 'polite');
    errorDiv.innerHTML = `
      <svg class="sticky-add-to-cart__error-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
      </svg>
      <span>${message}</span>
    `;
    
    // Insert error message above the button
    const actionsContainer = this.stickyBar.querySelector('.sticky-add-to-cart__actions');
    if (actionsContainer) {
      actionsContainer.insertBefore(errorDiv, actionsContainer.firstChild);
    }
  }
  
  hideErrorMessage() {
    const errorDiv = this.stickyBar.querySelector('.sticky-add-to-cart__error');
    if (errorDiv) {
      errorDiv.remove();
    }
  }
  
  // Utility functions
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Global initialization
window.initStickyATC = function() {
  // Remove existing instance
  if (window.stickyATCInstance) {
    window.stickyATCInstance = null;
  }
  
  // Create new instance
  window.stickyATCInstance = new StickyATC();
};

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.initStickyATC);
} else {
  window.initStickyATC();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StickyATC;
}